import {
	createActionsHook,
	createHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import { ConversationAssistantUIModes } from './types';

/**
 * Interface for controlling the conversation assistant sidebar panel state.
 * Consumers (like Confluence) can provide their own implementation
 * to integrate with their sidebar management system. Currently the
 * same interface as AISmartButtonSidebarAPI in @atlassian/ai-smart-button.
 */
export type RovoExperienceSidebarAPI = {
	isSidebarOpen: () => boolean;
	openSidebar: () => void;
	closeSidebar: () => void;
};

export type RovoExperienceAPIState = {
	mode?: ConversationAssistantUIModes;
	isMiniModalAvailable: boolean;
	isSidebarAvailable: boolean;
	isFullScreenModalAvailable: boolean;
	initialPrompt?: string;
	openWithChatHistory?: boolean;
	targetConversationId?: string | null;
	autoFocusInput?: boolean;
	wasAutoOpened: boolean;
	/**
	 * Set to true by the nudge onClick (before publish()) when the editor had
	 * DOM focus at click time. Consumed by useRestoreEditorFocusOnRovoClose to
	 * decide whether to restore editor focus when chat closes.
	 *
	 * This is needed because the nudge click moves DOM focus away from the
	 * editor before rovoMode transitions, so the normal snapshot-at-open-time
	 * approach always sees the nudge button as the active element instead of
	 * the editor.
	 */
	editorHadFocusBeforeChatOpen: boolean;
};

const initialState: RovoExperienceAPIState = {
	mode: undefined,
	isMiniModalAvailable: false,
	isSidebarAvailable: true,
	isFullScreenModalAvailable: false,
	initialPrompt: undefined,
	openWithChatHistory: false,
	targetConversationId: null,
	autoFocusInput: true,
	wasAutoOpened: false,
	editorHadFocusBeforeChatOpen: false,
};

const store = createStore({
	name: 'rovoExperienceAPIStore',
	initialState,
	actions: {
		// created for resetting store during tests
		reset:
			(): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState(initialState);
			},
		setMode:
			(
				mode: ConversationAssistantUIModes,
			): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ mode, wasAutoOpened: false });
			},
		clearInitialPrompt:
			() =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ initialPrompt: undefined });
			},
		setOpenWithChatHistory:
			(openWithChatHistory: boolean) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ openWithChatHistory });
			},
		setTargetConversationId:
			(targetConversationId: string | null) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ targetConversationId });
			},
		setWasAutoOpened:
			(wasAutoOpened: boolean) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ wasAutoOpened });
			},
		setEditorHadFocusBeforeChatOpen:
			(editorHadFocusBeforeChatOpen: boolean) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ editorHadFocusBeforeChatOpen });
			},
		toggleConversationAssistantUIMode:
			({
				targetMode,
				sidebarApi,
				initialPrompt,
				autoFocusInput = true,
			}: {
				targetMode?: ConversationAssistantUIModes;
				sidebarApi: RovoExperienceSidebarAPI;
				initialPrompt?: string;
				autoFocusInput?: boolean;
			}): (({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => {
				const { mode: currentMode } = getState();

				// If no target mode is provided, close any open modes
				if (!targetMode) {
					setState({ mode: undefined, autoFocusInput: true, wasAutoOpened: false });

					if (sidebarApi.isSidebarOpen()) {
						sidebarApi.closeSidebar();
					}

					return;
				}

				// Toggle mode functionality
				if (currentMode === targetMode) {
					switch (targetMode) {
						case ConversationAssistantUIModes.SIDEBAR: {
							/* Handles the edge case where the sidebar space is taken by another panel
							which results in currentMode = 'sidebar' && sidebarApi.isSidebarOpen() = false */
							if (!sidebarApi.isSidebarOpen()) {
								sidebarApi.openSidebar();
								return;
							}

							sidebarApi.closeSidebar();
							setState({ mode: undefined, autoFocusInput: true, wasAutoOpened: false });
							return;
						}
						case ConversationAssistantUIModes.MINI_MODAL: {
							setState({ mode: undefined, autoFocusInput: true, wasAutoOpened: false });
							return;
						}
						case ConversationAssistantUIModes.FULL_SCREEN_MODAL: {
							setState({ mode: undefined, autoFocusInput: true, wasAutoOpened: false });
							return;
						}
						default:
							return;
					}
				}

				// Switch mode functionality
				switch (targetMode) {
					case ConversationAssistantUIModes.SIDEBAR: {
						if (!sidebarApi.isSidebarOpen()) {
							sidebarApi.openSidebar();
						}
						setState({
							mode: ConversationAssistantUIModes.SIDEBAR,
							autoFocusInput,
							wasAutoOpened: false,
						});
						return;
					}

					case ConversationAssistantUIModes.MINI_MODAL: {
						if (sidebarApi.isSidebarOpen()) {
							sidebarApi.closeSidebar();
						}
						setState({
							mode: ConversationAssistantUIModes.MINI_MODAL,
							initialPrompt,
							autoFocusInput,
							wasAutoOpened: false,
						});
						return;
					}

					case ConversationAssistantUIModes.FULL_SCREEN_MODAL: {
						if (sidebarApi.isSidebarOpen()) {
							sidebarApi.closeSidebar();
						}
						setState({
							mode: ConversationAssistantUIModes.FULL_SCREEN_MODAL,
							initialPrompt,
							autoFocusInput,
							wasAutoOpened: false,
						});
						return;
					}
				}
			},
		openConversationAssistantUIMode:
			({
				targetMode,
				sidebarApi,
				initialPrompt,
				autoFocusInput = true,
			}: {
				targetMode: ConversationAssistantUIModes;
				sidebarApi: RovoExperienceSidebarAPI;
				initialPrompt?: string;
				autoFocusInput?: boolean;
			}): (({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => {
				const { mode: currentMode } = getState();

				// Already in the desired mode — ensure the sidebar is actually
				// visible (it may have been displaced by another panel) but
				// otherwise treat this as a no-op.
				if (currentMode === targetMode) {
					if (targetMode === ConversationAssistantUIModes.SIDEBAR && !sidebarApi.isSidebarOpen()) {
						sidebarApi.openSidebar();
					}
					return;
				}

				// Switch to the target mode
				switch (targetMode) {
					case ConversationAssistantUIModes.SIDEBAR: {
						if (!sidebarApi.isSidebarOpen()) {
							sidebarApi.openSidebar();
						}
						setState({
							mode: ConversationAssistantUIModes.SIDEBAR,
							autoFocusInput,
							wasAutoOpened: false,
						});
						return;
					}

					case ConversationAssistantUIModes.MINI_MODAL: {
						if (sidebarApi.isSidebarOpen()) {
							sidebarApi.closeSidebar();
						}
						// wasAutoOpened is managed by the dedicated setWasAutoOpened action and reset on close/toggle/setMode.
						setState({
							mode: ConversationAssistantUIModes.MINI_MODAL,
							initialPrompt,
							autoFocusInput,
						});
						return;
					}

					case ConversationAssistantUIModes.FULL_SCREEN_MODAL: {
						if (sidebarApi.isSidebarOpen()) {
							sidebarApi.closeSidebar();
						}
						setState({
							mode: ConversationAssistantUIModes.FULL_SCREEN_MODAL,
							initialPrompt,
							autoFocusInput,
						});
						return;
					}
				}
			},
		setIsMiniModalAvailable:
			(
				isMiniModalAvailable: boolean,
			): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ isMiniModalAvailable });
			},
		setIsSidebarAvailable:
			(
				isSidebarAvailable: boolean,
			): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ isSidebarAvailable });
			},
		setIsFullScreenModalAvailable:
			(
				isFullScreenModalAvailable: boolean,
			): (({ setState }: StoreActionApi<RovoExperienceAPIState>) => void) =>
			({ setState }: StoreActionApi<RovoExperienceAPIState>) => {
				setState({ isFullScreenModalAvailable });
			},
	},
});

const useStore = createHook(store);

export type RovoExperienceAPIStore = {
	mode?: ConversationAssistantUIModes;
	initialPrompt?: string;
	openWithChatHistory?: boolean;
	targetConversationId?: string | null;
	autoFocusInput?: boolean;
	wasAutoOpened?: boolean;
	editorHadFocusBeforeChatOpen?: boolean;
	reset: () => void;
	clearInitialPrompt: () => void;
	setOpenWithChatHistory?: (openWithChatHistory: boolean) => void;
	setTargetConversationId?: (targetConversationId: string | null) => void;
	setWasAutoOpened?: (wasAutoOpened: boolean) => void;
	setEditorHadFocusBeforeChatOpen?: (editorHadFocusBeforeChatOpen: boolean) => void;
	toggleConversationAssistantUIMode: (args: {
		targetMode?: ConversationAssistantUIModes;
		sidebarApi: RovoExperienceSidebarAPI;
		initialPrompt?: string;
		autoFocusInput?: boolean;
	}) => void;
	openConversationAssistantUIMode: (args: {
		targetMode: ConversationAssistantUIModes;
		sidebarApi: RovoExperienceSidebarAPI;
		initialPrompt?: string;
		autoFocusInput?: boolean;
	}) => void;
	setIsMiniModalAvailable: (isMiniModalAvailable: boolean) => void;
	setIsSidebarAvailable: (isSidebarAvailable: boolean) => void;
	setIsFullScreenModalAvailable: (isFullScreenModalAvailable: boolean) => void;
	isMiniModalAvailable: boolean;
	isSidebarAvailable: boolean;
	isFullScreenModalAvailable: boolean;
};

export const useRovoExperienceAPIStore = (): RovoExperienceAPIStore => {
	const [
		{
			mode,
			isMiniModalAvailable,
			isSidebarAvailable,
			isFullScreenModalAvailable,
			initialPrompt,
			openWithChatHistory,
			targetConversationId,
			autoFocusInput,
			wasAutoOpened,
			editorHadFocusBeforeChatOpen,
		},
		{
			reset,
			clearInitialPrompt,
			setOpenWithChatHistory,
			setTargetConversationId,
			setWasAutoOpened,
			setEditorHadFocusBeforeChatOpen,
			toggleConversationAssistantUIMode,
			openConversationAssistantUIMode,
			setIsMiniModalAvailable,
			setIsSidebarAvailable,
			setIsFullScreenModalAvailable,
		},
	] = useStore();

	return {
		mode,
		initialPrompt,
		openWithChatHistory,
		targetConversationId,
		autoFocusInput,
		wasAutoOpened,
		editorHadFocusBeforeChatOpen,
		reset,
		clearInitialPrompt,
		setOpenWithChatHistory,
		setTargetConversationId,
		setWasAutoOpened,
		setEditorHadFocusBeforeChatOpen,
		toggleConversationAssistantUIMode,
		openConversationAssistantUIMode,
		setIsMiniModalAvailable,
		setIsSidebarAvailable,
		setIsFullScreenModalAvailable,
		isMiniModalAvailable,
		isSidebarAvailable,
		isFullScreenModalAvailable,
	};
};

export const useRovoExperienceAPIStoreActions: HookActionsFunction<
	BoundActions<
		RovoExperienceAPIState,
		{
			// created for resetting store during tests
			reset: () => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setMode: (
				mode: ConversationAssistantUIModes,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setWasAutoOpened: (
				wasAutoOpened: boolean,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			toggleConversationAssistantUIMode: ({
				targetMode,
				sidebarApi,
			}: {
				targetMode?: ConversationAssistantUIModes;
				sidebarApi: RovoExperienceSidebarAPI;
				initialPrompt?: string;
				autoFocusInput?: boolean;
			}) => ({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			openConversationAssistantUIMode: ({
				targetMode,
				sidebarApi,
				initialPrompt,
			}: {
				targetMode: ConversationAssistantUIModes;
				sidebarApi: RovoExperienceSidebarAPI;
				initialPrompt?: string;
				autoFocusInput?: boolean;
			}) => ({ getState, setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setIsMiniModalAvailable: (
				isMiniModalAvailable: boolean,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setIsSidebarAvailable: (
				isSidebarAvailable: boolean,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
			setIsFullScreenModalAvailable: (
				isFullScreenModalAvailable: boolean,
			) => ({ setState }: StoreActionApi<RovoExperienceAPIState>) => void;
		}
	>
> = createActionsHook(store);
